import { LogOut, MailWarning, ShieldCheck } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';

import { AvatarUploader } from '@/components/shared/avatar-uploader';
import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { toast } from '@/components/ui/toast';
import { useResendVerification } from '@/hooks/use-auth';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { isSafeReturnTo } from '@/lib/safe-return-to';
import { uploadBlob } from '@/lib/upload';
import { TotpSection } from '@/pages/security/totp-section';
import { PasswordSection } from '@/pages/account/password-section';
import { useAuthStore, type AuthenticatedUser } from '@/stores/auth-store';
import { formatDateTime } from '@/utils/format';

const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
};

/**
 * The signed-in person's own account — profile photo, sign-in details and security.
 */
export const AccountPage = () => {
  const { t } = useTranslation('auth');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const location = useLocation();

  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const signOut = useAuthStore((state) => state.signOut);
  const resend = useResendVerification();

  const locationState = location.state as { returnTo?: unknown } | null;
  const returnTo = isSafeReturnTo(locationState?.returnTo)
    ? (locationState as { returnTo: string }).returnTo
    : null;

  useEffect(() => {
    if (!returnTo) return;
    document.getElementById('account-totp')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [returnTo]);

  if (!user) return null;

  const fullName = `${user.firstName} ${user.lastName}`;
  const localeLabel = LOCALE_LABELS[user.locale] ?? user.locale;

  const signOutAndLeave = async () => {
    await signOut();
    void navigate('/sign-in', { replace: true });
  };

  const continueWhereLeftOff = () => {
    if (!returnTo) return;
    void navigate(returnTo);
  };

  const saveAvatar = async (avatarKey: string | null) => {
    try {
      const updated = await api.patch<AuthenticatedUser>('/auth/me/avatar', { avatarKey });
      setUser(updated);
      toast.success(t('profile.photoSaved'));
    } catch (err) {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
      throw err;
    }
  };

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <PageHeader title={t('account.tabs.profile')} description={t('profile.description')} />

      {!user.isEmailVerified ? (
        <AccountBanner
          icon={<MailWarning className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />}
          tone="warning"
          message={t('banner.verifyEmail')}
          action={
            <Button
              size="sm"
              variant="outline"
              loading={resend.isPending}
              onClick={() =>
                resend.mutate(user.email, {
                  onSuccess: () => toast.success(t('verifyEmail.resent')),
                })
              }
            >
              {t('banner.verifyEmailAction')}
            </Button>
          }
        />
      ) : null}

      {returnTo ? (
        <AccountBanner
          icon={<ShieldCheck className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />}
          tone="neutral"
          message={
            user.isTotpEnabled ? t('banner.continueAfterTotp') : t('banner.setupTotpToContinue')
          }
          action={
            user.isTotpEnabled ? (
              <Button size="sm" onClick={continueWhereLeftOff}>
                {t('banner.continueAction')}
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() =>
                  document.getElementById('account-totp')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                  })
                }
              >
                {t('banner.setupTotpAction')}
              </Button>
            )
          }
        />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>{t('profile.identityTitle')}</CardTitle>
          <CardDescription>{t('profile.identityDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            <AvatarUploader
              src={user.avatarUrl}
              name={fullName}
              className="w-full max-w-none shrink-0 flex-col items-center gap-4 sm:flex-row lg:w-44 lg:flex-col"
              onUpload={async (blob) => {
                const { key } = await uploadBlob({
                  purpose: 'user_avatar',
                  blob,
                  withoutChurch: true,
                });
                await saveAvatar(key);
              }}
              onRemove={() => void saveAvatar(null)}
              labels={{
                change: t('profile.photoChange'),
                remove: t('profile.photoRemove'),
                cropTitle: t('profile.photoCropTitle'),
                zoom: tCommon('media.zoom'),
                apply: t('profile.photoApply'),
                cancel: tCommon('action.cancel'),
                instructions: t('profile.photoInstructions'),
                closeCropper: tCommon('action.close'),
              }}
            />

            <div className="min-w-0 flex-1 space-y-4">
              <div>
                <p className="text-section-title font-semibold text-fg">{fullName}</p>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <p className="text-body-sm text-fg-secondary">{user.email}</p>
                  {user.isEmailVerified ? (
                    <Badge variant="success">{t('profile.emailVerified')}</Badge>
                  ) : (
                    <Badge variant="warning">{t('profile.emailUnverified')}</Badge>
                  )}
                </div>
              </div>

              <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
                {user.phone ? (
                  <ProfileDetail label={tCommon('field.phone')} value={user.phone} />
                ) : null}
                <ProfileDetail
                  label={tCommon('field.status')}
                  value={
                    <Badge variant={user.status === 'active' ? 'success' : 'warning'}>
                      {tCommon(`status.${user.status}`)}
                    </Badge>
                  }
                />
                {user.lastSignInAt ? (
                  <ProfileDetail
                    label={tCommon('field.lastSignInAt')}
                    value={formatDateTime(user.lastSignInAt)}
                  />
                ) : null}
              </dl>

              <div className="grid gap-4 border-t border-border pt-6 sm:grid-cols-2">
                <FormField label={tCommon('field.locale')} tipId="account.locale">
                  {(field) => (
                    <p id={field.id} className="text-body text-fg">
                      {localeLabel}
                    </p>
                  )}
                </FormField>
                <FormField label={tCommon('field.timezone')} tipId="account.timezone">
                  {(field) => (
                    <p id={field.id} className="text-body text-fg">
                      {user.timezone}
                    </p>
                  )}
                </FormField>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2 lg:items-start">
        <PasswordSection />
        <TotpSection />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('profile.sessionTitle')}</CardTitle>
          <CardDescription>{t('profile.sessionDescription')}</CardDescription>
        </CardHeader>
        <CardFooter>
          <Button variant="outline" onClick={() => void signOutAndLeave()} leadingIcon={<LogOut className="size-4" aria-hidden />}>
            {t('signOut')}
          </Button>
        </CardFooter>
      </Card>

      <Card className="border-destructive/30">
        <CardHeader>
          <CardTitle>{t('deleteAccount.title')}</CardTitle>
          <CardDescription>{t('deleteAccount.cardDescription')}</CardDescription>
        </CardHeader>
        <CardFooter className="flex justify-end">
          <Button variant="outline" asChild>
            <Link to="/account/delete">{t('deleteAccount.open')}</Link>
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

const AccountBanner = ({
  icon,
  tone,
  message,
  action,
}: {
  icon: ReactNode;
  tone: 'warning' | 'neutral';
  message: string;
  action?: ReactNode;
}) => (
  <div
    className={
      tone === 'warning'
        ? 'flex flex-col gap-3 rounded-lg border border-warning/30 bg-warning/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between'
        : 'flex flex-col gap-3 rounded-lg border border-border bg-surface-secondary px-4 py-3 sm:flex-row sm:items-center sm:justify-between'
    }
  >
    <div className="flex items-start gap-2 text-body-sm text-fg">
      {icon}
      <span>{message}</span>
    </div>
    {action ? <div className="flex justify-end">{action}</div> : null}
  </div>
);

const ProfileDetail = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="grid grid-cols-[8rem_1fr] gap-2 text-body-sm sm:grid-cols-[7rem_1fr]">
    <dt className="text-fg-secondary">{label}</dt>
    <dd className="min-w-0 break-words text-fg">{value}</dd>
  </div>
);
